import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Formik, Form, FormikProps } from 'formik';
import { Player } from '@lordicon/react';
import { Alert, Button, Col, Container, Row, Spinner } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import {
  useTranslator,
  useAppContext,
  useToast,
  formatIsoDate,
  formatIsoDateTime,
  callNativeMobileApp,
  withMobileAppMode,
  FormikInput,
  FormikCheckboxInput as Checkbox,
  cloudflareImage,
  type AddQuickMealFn,
  type EditQuickMealFn,
} from '@front-activerse/common-components';
import Layout from '../../Layout/Layout';
import TitleBar from '../../Layout/TitleBar';
import {
  TrackerMealImage,
  TrackerMealFormHeader,
  TrackerMealSaveAsUserMealButton,
  TrackerMealRemoveModal,
  TrackerMealNameModal,
} from '../components/TrackerMeal';
import type { UnifiedMealData } from '../types';
import quickMealValidationSchema from './validationSchema';
import {
  QuickMealData,
  QuickMealFormValues,
  quickMealToFormValues,
  toAddQuickMealInput,
  toEditQuickMealInput,
  hasQuickMealCaloriesMismatch,
  hasNutritionChanged,
} from './helpers';
import { numericInputGuards } from '../helpers/numericInput';

export interface TrackerQuickMealFormProps {
  /** Per-portion values (see `normalizeQuickMeal`). */
  mealData: QuickMealData;
  /** Your Meals variants: no date/time, the meal is (or becomes) a template. */
  fromUserMeals?: boolean;
  /** Create modes (tracker log or Your Meals template). */
  addQuickMeal?: AddQuickMealFn;
  /** Edit modes (logged meal or template details). */
  editQuickMeal?: EditQuickMealFn;
  /** "Log this template" mode: date/time/servings for an existing Your Meals quick meal. */
  addUserMeal?: (input: {
    dateTime: string;
    baseMealId: number;
    portions: number;
    imageUrl?: string;
  }) => Promise<{ __typename: string; [key: string]: unknown }>;
  removeMeal?: (mealId: number) => Promise<{ code?: number; [key: string]: unknown }>;
  saveAsUserMeal?: (input: {
    loggedMealId: number;
  }) => Promise<{ __typename: string; [key: string]: unknown }>;
  /** Tracker day the screen was opened from (edit of a logged meal / log of a template). */
  date?: string;
  mobileAppMode?: boolean;
  /** Opened as a native modal: success closes it with a native toast instead of navigating. */
  webviewModal?: boolean;
}

const getCurrentTime = () => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
};

const timeFromIso = (iso?: string): string | null => {
  const match = iso?.match(/T(\d{2}):(\d{2})/);
  return match ? `${match[1]}:${match[2]}` : null;
};

const goBackToOrigin = () => -Number(sessionStorage.getItem('tracker_depth') || 1);

/**
 * The quick-meal screen: name + nutrition values per portion, no ingredients. One component serves
 * five flows, told apart by which mutation it receives:
 *
 * - `addQuickMeal`                      → log to tracker (date/time/servings/"save to Your Meals")
 * - `addQuickMeal` + `fromUserMeals`    → create a Your Meals template
 * - `editQuickMeal`                     → edit a logged meal
 * - `editQuickMeal` + `fromUserMeals`   → Your Meals template details (notes, read-only servings)
 * - `addUserMeal`                       → log a Your Meals quick-meal template to the tracker
 *
 * Calories are checked against the Atwater estimate of the macros before saving; a large drift arms
 * a warning and turns the footer into Cancel / Confirm, exactly like the add-product form.
 */
const TrackerQuickMealForm: React.FC<TrackerQuickMealFormProps> = ({
  mealData,
  fromUserMeals = false,
  addQuickMeal,
  editQuickMeal,
  addUserMeal,
  removeMeal,
  saveAsUserMeal,
  date: dateProp,
  mobileAppMode = false,
  webviewModal = false,
}) => {
  const { t } = useTranslator();
  const { toastError, toastSuccess } = useToast();
  const navigate = useNavigate();
  const {
    consts: { APP_BRAND },
    images: {
      editPrimaryIcon,
      editNeutralIcon,
      yourMealsAddIcon,
      yourMealsSavedIcon,
      yourMealsRemoveIcon,
      addToTrackerIcon,
      cameraIcon,
      addPlusIcon,
    },
    lotties: { trashIcon },
  } = useAppContext();

  const isEditMode = Boolean(editQuickMeal);
  const isLogTemplateMode = Boolean(addUserMeal) && !isEditMode;
  const isTemplateDetails = isEditMode && fromUserMeals;
  const showDateTime = !fromUserMeals;
  const showServingsInput = !fromUserMeals || isLogTemplateMode;
  const showSaveYourMealCheckbox = !isEditMode && !isLogTemplateMode && !fromUserMeals;

  const formikRef = useRef<FormikProps<QuickMealFormValues> | null>(null);
  const servingsInputRef = useRef<HTMLInputElement>(null);
  const calorieWarningRef = useRef<HTMLDivElement>(null);
  const playerRefTrash = useRef<Player>(null);
  const iconsIconErrorColor = useMemo(() => {
    if (typeof document === 'undefined') return '#000000';
    const color = getComputedStyle(document.documentElement)
      .getPropertyValue('--icons-icon-error-color')
      .trim();
    return color || '#000000';
  }, []);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [calorieWarningArmed, setCalorieWarningArmed] = useState(false);
  const [customImageUrl, setCustomImageUrl] = useState<string | null>(null);
  const [isSavedToUserMeals, setIsSavedToUserMeals] = useState(mealData.isFromBaseMeal);
  const [isSavingToUserMeals, setIsSavingToUserMeals] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showMealNameModal, setShowMealNameModal] = useState(false);

  // Photo uploaded on /tracker/upload-image while this screen stayed mounted.
  useEffect(() => {
    const handlePhotoUploaded = (e: Event) => {
      const imageUrl = (e as CustomEvent<{ imageUrl: string }>).detail?.imageUrl;
      if (imageUrl) setCustomImageUrl(imageUrl);
    };
    window.addEventListener('activityPhotoUploaded', handlePhotoUploaded);
    return () => window.removeEventListener('activityPhotoUploaded', handlePhotoUploaded);
  }, []);

  const today = formatIsoDate(new Date());
  const todayDate = new Date();
  todayDate.setHours(0, 0, 0, 0);
  const minDateObj = new Date(todayDate);
  minDateObj.setDate(minDateObj.getDate() - 30);
  const maxDateObj = new Date(todayDate);
  maxDateObj.setDate(maxDateObj.getDate() + 30);

  const loggedDate =
    isEditMode && mealData.loggedAt ? formatIsoDate(new Date(mealData.loggedAt)) : null;
  const initialDate = loggedDate ?? dateProp ?? today;
  const initialTime = (isEditMode ? timeFromIso(mealData.loggedAt) : null) ?? getCurrentTime();
  const [showDateInput, setShowDateInput] = useState(initialDate !== today);

  const initialValues = useMemo<QuickMealFormValues>(
    () =>
      quickMealToFormValues(mealData, {
        ...(showDateTime ? { date: initialDate, time: initialTime } : {}),
        // A template logged to the tracker starts from one portion, whatever it was saved with.
        servings: isLogTemplateMode ? 1 : mealData.portions,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mealData.id]
  );

  const validationSchema = useMemo(
    () =>
      quickMealValidationSchema({ withDateTime: showDateTime, withServings: showServingsInput }),
    [showDateTime, showServingsInput]
  );

  const imageForDisplay = useMemo<UnifiedMealData>(
    () =>
      ({
        id: mealData.id,
        name: mealData.name,
        kcal: mealData.calories,
        preparationImageUrl: mealData.imageUrl,
        sourceType: 'manual',
        ingredients: [],
        portions: mealData.portions,
      } as UnifiedMealData),
    [mealData]
  );

  const title =
    isEditMode || fromUserMeals
      ? t('tracker.mealDetails.navbarTitle')
      : t('tracker.logMeal.navbarTitle');

  // Calories are an Int on the API; macros keep two decimals.
  const integerGuards = useMemo(() => numericInputGuards({ decimals: false }), []);
  const decimalGuards = useMemo(() => numericInputGuards({ decimals: true }), []);

  const resetCalorieWarning = useCallback(() => setCalorieWarningArmed(false), []);

  const scrollToCalorieWarning = () => {
    requestAnimationFrame(() => {
      const el = calorieWarningRef.current;
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  };

  const closeScreen = () => {
    if (webviewModal && mobileAppMode) {
      setTimeout(() => callNativeMobileApp({ action: 'close_modal' }), 0);
    } else {
      navigate(goBackToOrigin());
    }
  };

  const handleGoToTracker = (date: string) => {
    if (mobileAppMode) {
      callNativeMobileApp({ action: 'open_modal', payload: { path: `/tracker/${date}` } });
    } else {
      navigate(`/tracker/${date}`);
    }
  };

  const refreshNativeTracker = (date: string) => {
    callNativeMobileApp({ action: 'refresh_data', payload: { module: 'tracker', date } });
  };

  const finishAdd = (dateForRefresh: string) => {
    refreshNativeTracker(dateForRefresh);
    if (webviewModal && mobileAppMode) {
      setTimeout(() => {
        callNativeMobileApp({
          action: 'show_toast',
          payload: {
            type: 'success',
            content: fromUserMeals
              ? `${t('yourMeal.notification.savedInBase.content')}`
              : `${t('meals.notification.trackerConfirmation.content')}`,
          },
        });
        callNativeMobileApp({ action: 'close_modal' });
      }, 0);
      return;
    }
    navigate(goBackToOrigin());
    if (fromUserMeals) {
      toastSuccess(<p className="mb-0 w-100">{t('yourMeal.notification.savedInBase.content')}</p>);
    } else {
      toastSuccess(
        <p className="mb-0 w-100">
          {t('meals.notification.trackerConfirmation.content')}
          <Button color="link" onClick={() => handleGoToTracker(dateForRefresh)}>
            {t('meals.notification.trackerConfirmation.button')}
          </Button>
        </p>
      );
    }
  };

  const handleSubmit = async (values: QuickMealFormValues) => {
    if (!calorieWarningArmed && hasQuickMealCaloriesMismatch(values)) {
      setCalorieWarningArmed(true);
      scrollToCalorieWarning();
      return;
    }

    setIsSubmitting(true);
    const dateTime = showDateTime
      ? formatIsoDateTime(values.date as string, `${values.time}:00`)
      : undefined;
    const dateForRefresh = showDateTime ? (values.date as string) : today;
    const imageUrl = customImageUrl || mealData.imageUrl || undefined;

    try {
      if (isEditMode && editQuickMeal) {
        const response = await editQuickMeal(
          toEditQuickMealInput(values, {
            mealId: mealData.id,
            dateTime,
            imageUrl: customImageUrl || undefined,
            notes: isTemplateDetails ? values.notes : undefined,
          })
        );
        if (response?.__typename !== 'DailyTrackerEditQuickMealSuccess') {
          toastError();
          return;
        }
        refreshNativeTracker(dateForRefresh);
        if (dateProp && dateProp !== dateForRefresh) refreshNativeTracker(dateProp);
        toastSuccess(<p>{t('meals.notification.editMealConfirmation.content')}</p>);
        if (isTemplateDetails) {
          setShowNotes(false);
          formikRef.current?.resetForm({ values });
          setCalorieWarningArmed(false);
        } else {
          navigate(goBackToOrigin());
        }
        return;
      }

      if (isLogTemplateMode && addUserMeal && dateTime && !hasNutritionChanged(mealData, values)) {
        const response = await addUserMeal({
          dateTime,
          baseMealId: mealData.id,
          portions: Number(values.servings),
          imageUrl,
        });
        if (response?.__typename !== 'DailyTrackerAddUserMealSuccess') {
          toastError();
          return;
        }
        finishAdd(dateForRefresh);
        return;
      }

      if (!addQuickMeal) {
        toastError();
        return;
      }
      const response = await addQuickMeal(
        toAddQuickMealInput(values, { dateTime, imageUrl }),
        values.saveYourMeal
      );
      if (response?.__typename !== 'DailyTrackerAddQuickMealSuccess') {
        toastError();
        return;
      }
      const mealId = response.meal?.id;
      if (values.saveYourMeal && mealId && saveAsUserMeal) {
        try {
          await saveAsUserMeal({ loggedMealId: mealId });
        } catch (error) {
          console.error('Error saving quick meal as user meal:', error);
        }
      }
      finishAdd(dateForRefresh);
    } catch (error) {
      toastError();
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeMealHandler = async () => {
    setShowRemoveModal(false);
    if (!removeMeal) {
      toastError();
      return;
    }
    setIsRemoving(true);
    try {
      const response = await removeMeal(mealData.id);
      if (response?.code === 200) {
        refreshNativeTracker(dateProp ?? today);
        if (isTemplateDetails) {
          toastSuccess(
            <p className="w-100">{t('yourMeal.notification.removedFromBase.content')}</p>
          );
        }
        navigate(-1);
      } else {
        toastError();
      }
    } catch (error) {
      toastError();
    } finally {
      setIsRemoving(false);
    }
  };

  const saveAsUserMealHandler = async () => {
    if (isSavedToUserMeals || !saveAsUserMeal) return;
    setIsSavingToUserMeals(true);
    try {
      const response = await saveAsUserMeal({ loggedMealId: mealData.id });
      if (response?.__typename === 'DailyTrackerSaveAsUserMealSuccess') {
        setIsSavedToUserMeals(true);
      } else {
        toastError();
      }
    } catch (error) {
      toastError();
    } finally {
      setIsSavingToUserMeals(false);
    }
  };

  const handleDeleteLinkClick = () => {
    if (isEditMode) {
      setShowRemoveModal(true);
    } else {
      closeScreen();
    }
  };

  const submitLabel = () => {
    // Same copy as the add-product form once its calorie warning is armed, in every mode.
    if (calorieWarningArmed) return t('tracker.addProduct.confirmSave.button');
    if (isEditMode) return t('tracker.mealDetails.saveButton');
    if (fromUserMeals) return t('yourMeal.addToMealsButton');
    return t('tracker.meal.logButton');
  };

  const templateCoverImage = cloudflareImage(
    mealData.imageUrl || '',
    `${APP_BRAND}`,
    'cookbookMealDetails'
  );

  const goToLogTemplate = () =>
    navigate(`/meals/user-meals/module/tracker/add-meal/${mealData.id}`);

  return (
    <>
      <Layout>
        <TitleBar showBackBtn={true}>{title}</TitleBar>

        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnMount
          enableReinitialize
        >
          {({ values, isValid, dirty, setFieldTouched, setFieldValue, validateField }) => {
            const onNutritionChange = (field: string) => {
              resetCalorieWarning();
              setFieldTouched(field, true, false);
            };
            const footerVisible = !isEditMode || dirty || Boolean(customImageUrl);
            const showLogTemplateCta = isTemplateDetails && !dirty && !customImageUrl;

            const nutritionFields = (
              <>
                <FormikInput
                  name="calories"
                  label={t('addIngredient.calories')}
                  unit={t('calories.unit')}
                  inputMode="decimal"
                  {...integerGuards}
                  autoComplete="off"
                  maxLength={7}
                  translateErrMsg
                  callbackOnChange={() => onNutritionChange('calories')}
                  data-testid="quick-meal-calories"
                  data-test-id="quick_meal_calories"
                />
                <Row>
                  <Col xs="6">
                    <FormikInput
                      name="protein"
                      label={t('addIngredient.proteins')}
                      unit="g"
                      inputMode="decimal"
                      {...decimalGuards}
                      autoComplete="off"
                      maxLength={7}
                      translateErrMsg
                      callbackOnChange={() => onNutritionChange('protein')}
                      data-testid="quick-meal-protein"
                      data-test-id="quick_meal_protein"
                    />
                  </Col>
                  <Col xs="6">
                    <FormikInput
                      name="fat"
                      label={t('addIngredient.fat')}
                      unit="g"
                      inputMode="decimal"
                      {...decimalGuards}
                      autoComplete="off"
                      maxLength={7}
                      translateErrMsg
                      callbackOnChange={() => onNutritionChange('fat')}
                      data-testid="quick-meal-fat"
                      data-test-id="quick_meal_fat"
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs="6">
                    <FormikInput
                      name="carbs"
                      label={t('addIngredient.carbs')}
                      unit="g"
                      inputMode="decimal"
                      {...decimalGuards}
                      autoComplete="off"
                      maxLength={7}
                      translateErrMsg
                      callbackOnChange={() => onNutritionChange('carbs')}
                      data-testid="quick-meal-carbs"
                      data-test-id="quick_meal_carbs"
                    />
                  </Col>
                  <Col xs="6">
                    <FormikInput
                      name="fiber"
                      label={t('addIngredient.fiber')}
                      unit="g"
                      inputMode="decimal"
                      {...decimalGuards}
                      autoComplete="off"
                      maxLength={7}
                      translateErrMsg
                      callbackOnChange={() => onNutritionChange('fiber')}
                      data-testid="quick-meal-fiber"
                      data-test-id="quick_meal_fiber"
                    />
                  </Col>
                </Row>
              </>
            );

            const calorieAlert = calorieWarningArmed && (
              <div ref={calorieWarningRef}>
                <Alert
                  color="warning"
                  fade={false}
                  className="mt-l mb-0"
                  data-testid="quick-meal-calorie-warning"
                  data-test-id="quick_meal_calorie_warning"
                >
                  <p className="mb-0">
                    {t('tracker.quickMeal.validationError.nutritionMacrosExceedLimit')}
                  </p>
                </Alert>
              </div>
            );

            const submitButton = (
              <Button
                type="submit"
                color="primary"
                className="w-100"
                disabled={isSubmitting || !isValid}
                data-testid="quick-meal-submit"
                data-test-id="quick_meal_submit"
              >
                {/* Spinner + "processing" only on the full-width button (TrackerMealStickyFooter
                    convention); the half-width Cancel/Confirm pair just disables, like the
                    add-product footer, because the text does not fit there. */}
                {isSubmitting && !calorieWarningArmed ? (
                  <>
                    <Spinner />
                    <span className="ms-2">{t('data-processing')}</span>
                  </>
                ) : (
                  submitLabel()
                )}
              </Button>
            );

            const submitRow = (
              <Row>
                {calorieWarningArmed && (
                  <Col xs="6">
                    <Button
                      type="button"
                      color="secondary"
                      outline
                      className="w-100"
                      onClick={closeScreen}
                      data-testid="quick-meal-cancel"
                      data-test-id="quick_meal_cancel"
                    >
                      {t('tracker.addProduct.cancelButton')}
                    </Button>
                  </Col>
                )}
                <Col xs={calorieWarningArmed ? 6 : 12}>{submitButton}</Col>
              </Row>
            );

            // ----- Your Meals template details: the Your Meals details shell (UserMealEditMeal)
            if (isTemplateDetails) {
              return (
                <Form noValidate data-testid="quick-meal-form">
                  <div className="page-template-user-meals">
                    <Container className="py-md-4 full-height4">
                      <Row>
                        <Col xl={{ size: 10, offset: 1 }}>
                          <div className="page-container">
                            <div className="meal-details">
                              <Row>
                                <Col md="6">
                                  <div className="meal-image-video">
                                    <img
                                      src={templateCoverImage}
                                      className="img-fluid"
                                      width={768}
                                      height={768}
                                      alt=""
                                    />
                                    <div className="meal-info2 d-md-none">
                                      <Container>
                                        <h1 className="meal-title title-large semi-bold white mb-0">
                                          {values.mealName}
                                        </h1>
                                      </Container>
                                    </div>
                                  </div>
                                </Col>
                                <Col md="6">
                                  <div className="meal-info">
                                    <div className="d-none d-md-block">
                                      <h1 className="meal-title title-large semi-bold mb-xl">
                                        <Button
                                          color="link"
                                          onClick={() => setShowMealNameModal(true)}
                                          className="float-end p-0"
                                        >
                                          <img src={editNeutralIcon} alt="edit" className="ms-1" />
                                        </Button>
                                        {values.mealName}
                                      </h1>
                                    </div>

                                    <div
                                      className="grey-box mb-3"
                                      data-testid="quick-meal-servings"
                                    >
                                      {t('mealDetails.info.servings.title')}{' '}
                                      <strong className="float-end">{mealData.portions}</strong>
                                    </div>

                                    <Button
                                      color="primary"
                                      size="sm"
                                      className="w-100 d-none d-md-block tracker"
                                      onClick={goToLogTemplate}
                                    >
                                      <img
                                        src={addToTrackerIcon}
                                        alt={t('tracker.meal.addToTrackerButton')}
                                        className="me-2"
                                      />
                                      {t('tracker.meal.addToTrackerButton')}
                                    </Button>
                                  </div>
                                </Col>
                              </Row>
                            </div>
                          </div>

                          {/* Inputs sit straight on the white page, as the ingredient list does
                              on the Your Meals details page (Figma yourMeal.details1). */}
                          <div className="tracker-ingredients-list">
                            <p className="text-text-main-color semi-bold mb-l">
                              {t('addIngredient.nutritionValues.title')}
                            </p>
                            {nutritionFields}
                            {calorieAlert}
                          </div>

                          <div className="user-notes">
                            <p className="text-text-main-color semi-bold d-flex justify-content-between w-100">
                              {t('mealDetails.notesBox.title')}
                              {showNotes && (
                                <Button
                                  color="link"
                                  size="sm"
                                  className="p-0"
                                  onClick={() => setShowNotes(false)}
                                >
                                  {t('mealDetails.notesBox.confirmButton')}
                                </Button>
                              )}
                              {values.notes !== '' && !showNotes && (
                                <Button
                                  color="link"
                                  size="sm"
                                  className="p-0"
                                  onClick={() => setShowNotes(true)}
                                >
                                  {t('mealDetails.notesBox.editButton')}
                                </Button>
                              )}
                            </p>
                            {showNotes ? (
                              <FormikInput
                                type="textarea"
                                name="notes"
                                className="w-100"
                                translateErrMsg
                                style={{ resize: 'none', overflow: 'hidden', minHeight: '60px' }}
                                data-testid="quick-meal-notes"
                              />
                            ) : values.notes !== '' ? (
                              <p className="text-text-variant-color text-prewrap">{values.notes}</p>
                            ) : (
                              <button
                                type="button"
                                className="add-note w-100"
                                onClick={() => setShowNotes(true)}
                              >
                                <img src={addPlusIcon} alt="" />
                                <span>{t('mealDetails.notesBox.addButton')}</span>
                              </button>
                            )}
                          </div>

                          <Button
                            type="button"
                            color="link"
                            className="w-100 mt-md-3"
                            onClick={removeMealHandler}
                            disabled={isRemoving}
                            data-testid="quick-meal-delete"
                            data-test-id="quick_meal_delete"
                          >
                            {isRemoving ? (
                              <>
                                <Spinner size="sm" />
                                <span className="ms-2">{t('data-processing')}</span>
                              </>
                            ) : (
                              <>
                                <img src={yourMealsRemoveIcon} alt="delete" className="me-2" />
                                <span className="text-text-error-color">
                                  {t('yourMeal.removeButton')}
                                </span>
                              </>
                            )}
                          </Button>
                        </Col>
                      </Row>
                    </Container>

                    <div className="sticky-footer">
                      <Container className="px-3">
                        <Row>
                          <Col xs="12" md={{ size: 4, offset: 4 }}>
                            {showLogTemplateCta ? (
                              <Button
                                type="button"
                                color="primary"
                                className="w-100 px-3 tracker"
                                onClick={goToLogTemplate}
                                data-testid="quick-meal-log-template"
                              >
                                <img
                                  src={addToTrackerIcon}
                                  alt={t('tracker.meal.addToTrackerButton')}
                                  className="me-2"
                                />
                                {t('tracker.meal.addToTrackerButton')}
                              </Button>
                            ) : (
                              submitRow
                            )}
                          </Col>
                        </Row>
                      </Container>
                    </div>
                  </div>

                  <TrackerMealNameModal
                    isOpen={showMealNameModal}
                    onClose={() => setShowMealNameModal(false)}
                    mealName={values.mealName}
                    onSave={({ mealName }) => {
                      setFieldValue('mealName', mealName ?? '');
                      setShowMealNameModal(false);
                    }}
                    t={t}
                  />
                </Form>
              );
            }

            // ----- Tracker flows: the custom-meal page shell (TrackerAddEditMeal)
            return (
              <Form noValidate data-testid="quick-meal-form">
                <div className="page-template-tracker">
                  <Container className="py-md-4 full-height4">
                    <Row>
                      <Col xl={{ size: 10, offset: 1 }}>
                        <div className="page-container">
                          <div className="tracker-container">
                            <Row>
                              <Col md="6">
                                <TrackerMealImage
                                  mealData={imageForDisplay}
                                  appBrand={APP_BRAND as string}
                                  customImageUrl={customImageUrl}
                                  cameraIcon={cameraIcon}
                                  onCameraClick={() => navigate('/tracker/upload-image')}
                                />
                              </Col>
                              <Col md="6">
                                <TrackerMealFormHeader
                                  showDateInput={showDateInput}
                                  minDate={formatIsoDate(minDateObj)}
                                  maxDate={formatIsoDate(maxDateObj)}
                                  mealName={values.mealName ?? ''}
                                  editPrimaryIcon={editPrimaryIcon}
                                  inputRef={servingsInputRef}
                                  onDateBlur={(value) => setShowDateInput(value !== today)}
                                  showDateTimeInputs={showDateTime}
                                  showServingsInput={showServingsInput}
                                  initialShowMealNameEdit={mealData.name === ''}
                                  autoFocusMealName={!isEditMode && mealData.name === ''}
                                  showMealNameConfirm={isEditMode}
                                  onServingsChange={() => {
                                    setTimeout(() => validateField('servings'), 0);
                                  }}
                                />
                              </Col>
                            </Row>
                          </div>
                        </div>

                        {isEditMode && (
                          <TrackerMealSaveAsUserMealButton
                            isSaved={isSavedToUserMeals}
                            isSaving={isSavingToUserMeals}
                            onClick={saveAsUserMealHandler}
                            savedIcon={yourMealsSavedIcon}
                            addIcon={yourMealsAddIcon}
                          />
                        )}

                        {/* Same section anatomy as the ingredients list on the custom-meal page:
                            a title on the page background and a white rounded card below it. The
                            card is a plain `.page-container`, not `.tracker-ingredients-list-items`,
                            whose styles shrink inputs to the ingredient-amount width. */}
                        {/* Figma: 32px from the card above to the title, 12px from the title to
                            the inputs card (wrapper padding-top 16 + mt-l). */}
                        <div className="tracker-ingredients-list mt-l">
                          <p className="text-text-main-color semi-bold mb-m">
                            {t('addIngredient.nutritionValues.title')}
                          </p>
                          <div className="page-container rounded-3 px-m py-m">
                            {nutritionFields}
                          </div>
                          {calorieAlert}
                        </div>

                        <Button
                          type="button"
                          color="link"
                          className="w-100 mb-xl mt-3 d-flex align-items-center justify-content-center"
                          onClick={handleDeleteLinkClick}
                          disabled={isRemoving}
                          onMouseEnter={() => playerRefTrash.current?.playFromBeginning()}
                          onTouchStart={() => playerRefTrash.current?.playFromBeginning()}
                          data-testid="quick-meal-delete"
                          data-test-id="quick_meal_delete"
                        >
                          {/* Same trash icon and wrapper as the custom-meal page's delete link
                              (TrackerAddEditMeal). */}
                          <div className="me-1 d-inline-block">
                            <Player
                              ref={playerRefTrash}
                              icon={trashIcon}
                              size={20}
                              colorize={iconsIconErrorColor}
                            />
                          </div>
                          <span className="text-text-error-color">{t('tracker.deleteButton')}</span>
                        </Button>
                      </Col>
                    </Row>
                  </Container>

                  {footerVisible && (
                    <div className="sticky-footer">
                      <Container className="px-3">
                        <Row>
                          <Col xs="12" md={{ size: 4, offset: 4 }}>
                            {showSaveYourMealCheckbox && (
                              <div
                                className={
                                  isValid
                                    ? 'save-your-meal-container'
                                    : 'save-your-meal-container opacity-50'
                                }
                              >
                                <Checkbox
                                  name="saveYourMeal"
                                  disabled={!isValid}
                                  data-testid="quick-meal-save-your-meal"
                                >
                                  <p className="mb-0 small text-text-variant-color">
                                    {t('tracker.logToTracker.saveYourMeal.checkbox')}
                                  </p>
                                </Checkbox>
                              </div>
                            )}
                            {submitRow}
                          </Col>
                        </Row>
                      </Container>
                    </div>
                  )}
                </div>
              </Form>
            );
          }}
        </Formik>
      </Layout>

      <TrackerMealRemoveModal
        isOpen={showRemoveModal}
        onToggle={() => setShowRemoveModal(!showRemoveModal)}
        onConfirm={removeMealHandler}
        t={t}
      />
    </>
  );
};

export default withMobileAppMode(TrackerQuickMealForm);
